/** Pure planning for undo of recategorize + category rename/move/delete. No
 *  React, no network — reads cached rows/meta and returns the inverse-mutation
 *  plan. The hook (useCategoryActions) calls these, then fires the mutations. */

import type { CategoryMeta } from 'markcat-domain';
import { splitCategoryPath, joinCategoryPath } from 'markcat-domain';

/** Minimal row shape the planners read from the bookmarks cache. */
export interface CategoryPathRow {
  id: string;
  category_path?: string | null;
}

/** Group [id, oldPath] pairs into [oldPath, ids] buckets, preserving order. */
function groupByOldPath(pairs: Array<[string, string]>): Array<[string, string[]]> {
  const groups = new Map<string, string[]>();
  for (const [id, oldPath] of pairs) {
    const list = groups.get(oldPath);
    if (list) list.push(id);
    else groups.set(oldPath, [id]);
  }
  return [...groups.entries()];
}

export interface RecategorizeUndoPlan {
  /** false when any id had no cached row — the caller then offers no undo. */
  complete: boolean;
  /** [oldPath, ids] buckets; '' = Uncategorized. */
  groups: Array<[string, string[]]>;
}

/** Snapshot each id's current category_path, grouped by old path. An id missing
 *  from the cache flips `complete` to false (partial undo is never offered). */
export function buildRecategorizeUndoPlan(
  ids: string[],
  rows: CategoryPathRow[],
): RecategorizeUndoPlan {
  const byId = new Map(rows.map((r) => [r.id, r.category_path ?? '']));
  const pairs: Array<[string, string]> = [];
  let complete = true;
  for (const id of ids) {
    const oldPath = byId.get(id);
    if (oldPath === undefined) { complete = false; continue; }
    pairs.push([id, oldPath]);
  }
  return { complete, groups: groupByOldPath(pairs) };
}

/** Inverse of a rename (only the last segment changed): rename new_path back to
 *  old_path's last segment. */
export function deriveRenameUndo(
  oldPath: string,
  newPath: string,
): { path: string; newName: string } {
  const segs = splitCategoryPath(oldPath);
  return { path: newPath, newName: segs[segs.length - 1] };
}

/** Inverse of a move: move new_path back under old_path's parent. A root parent
 *  (old_path was a single segment) is expressed as null, matching the move API. */
export function deriveMoveUndo(
  oldPath: string,
  newPath: string,
): { path: string; targetParent: string | null } {
  const parent = splitCategoryPath(oldPath).slice(0, -1);
  return { path: newPath, targetParent: parent.length ? joinCategoryPath(parent) : null };
}

export interface CategoryDeleteSnapshot {
  /** Flat subtree ids, for cascade=true bulk restore. */
  ids: string[];
  /** [oldPath, ids] buckets, for cascade=false re-home to old paths. */
  idGroups: Array<[string, string[]]>;
  /** Meta rows for path + descendants, re-POSTed on undo. */
  meta: CategoryMeta[];
  /** false when the meta cache was absent (undo can't restore icons/colors). */
  metaCaptured: boolean;
}

/** Snapshot everything a category delete destroys: the subtree bookmark ids
 *  (grouped by old path) and the category_meta rows for path + descendants. The
 *  subtree is `path` itself plus anything under `path + '>'`. */
export function buildCategoryDeleteSnapshot(
  path: string,
  rows: CategoryPathRow[],
  meta: CategoryMeta[] | undefined,
): CategoryDeleteSnapshot {
  const prefix = `${path}>`;
  const inSubtree = (p: string | null | undefined) => p === path || (p?.startsWith(prefix) ?? false);
  const pairs: Array<[string, string]> = rows
    .filter((b) => inSubtree(b.category_path))
    .map((b) => [b.id, b.category_path ?? ''] as [string, string]);
  return {
    ids: pairs.map(([id]) => id),
    idGroups: groupByOldPath(pairs),
    meta: (meta ?? []).filter((m) => m.path === path || m.path.startsWith(prefix)),
    metaCaptured: meta !== undefined,
  };
}
